import { computeBounds, pointToLatlng, type LatLng } from '@lc/libgeo'
import type { LatLngBounds } from '@lc/libmap-types'
import type { Polygon } from 'geojson'
import { useMemo } from 'react'
import { useDataConfigSpeed } from './data/useDataConfigSpeed'

export function useBuffer(
	latlng?: LatLng,
	inCity?: boolean,
	{ level, disable }: { level?: number; disable?: boolean } = {},
): {
	bounds?: LatLngBounds
	geojson?: Polygon
	lens?: Polygon
	points?: { highestPoint?: LatLng; lowestPoint?: LatLng }
	time: number
	meters: number
} {
	const { configSpeed } = useDataConfigSpeed({
		level: level,
		disable: disable || !latlng,
	})

	const [bounds, geojson, lens, highestPoint, lowestPoint, time, meters] =
		useMemo<
			| [LatLngBounds, Polygon, Polygon, LatLng, LatLng, number, number]
			| [undefined, undefined, undefined, undefined, undefined, 0, 0]
		>(() => {
			if (!latlng)
				return [
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					0,
					0,
				]

			if (!configSpeed)
				return [
					undefined,
					undefined,
					undefined,
					undefined,
					undefined,
					0,
					0,
				]

			const points = 75
			const buffer = inCity
				? configSpeed.bufferInUrbanZones
				: configSpeed.buffer
			const radius = configSpeed.walk.normal * buffer
			const exterior: [number, number][] = []
			const cutout: [number, number][] = []
			for (let i = 0; i < points; i++) {
				const angle = (i * 360) / points
				const offsetX = radius * Math.cos((angle * Math.PI) / 180)
				const offsetY = radius * Math.sin((angle * Math.PI) / 180)
				const offsetLng =
					offsetX / (111320 * Math.cos((latlng[0] * Math.PI) / 180))
				const offsetLat = offsetY / 110540
				exterior.push([
					latlng[1] + offsetLng * 1000,
					latlng[0] + offsetLat * 1000,
				])

				cutout.push([latlng[1] + offsetLng, latlng[0] + offsetLat])
			}
			exterior.push(exterior[0])
			cutout.push(cutout[0])

			// eslint-disable-next-line unicorn/no-array-reduce
			const highestPoint = cutout.reduce(
				(acc, cur) => (cur[1] > acc[1] ? cur : acc),
				cutout[0],
			)

			// eslint-disable-next-line unicorn/no-array-reduce
			const lowestPoint = cutout.reduce(
				(acc, cur) => (cur[1] < acc[1] ? cur : acc),
				cutout[0],
			)

			return [
				computeBounds([
					{
						type: 'Polygon',
						coordinates: [cutout],
					},
				]),
				{
					type: 'Polygon',
					coordinates: [cutout],
				},
				{
					type: 'Polygon',
					coordinates: [exterior, cutout],
				},
				pointToLatlng([highestPoint[0], highestPoint[1]]),
				pointToLatlng([lowestPoint[0], lowestPoint[1]]),
				buffer,
				radius,
			]
		}, [latlng, configSpeed, inCity])

	return {
		bounds,
		geojson,
		lens,
		points: { highestPoint, lowestPoint },
		time,
		meters,
	}
}
